import { NullOr } from "@regal-voice/shared-types";
import { notification } from "antd";
import intersection from "lodash/intersection";

import { hardRedirect } from "App/routes/navigation";
import { apolloMutation } from "Services/ApolloClientService";
import { getLogger } from "Services/LoggingService";
import { updateAgent } from "Services/marketing-api/agents/mutations";
import { disconnect as disconnectSSE } from "Services/server-sent-events";
import { selectAgentInformation } from "Services/state/agent/AgentInformationSlice";
import { clearBrand, selectBrandAvailableStatuses } from "Services/state/brand";
import { reduxStore } from "Services/state/Storage";

import { authState, brandSlugFromAuth } from "./authState";
import { apiHeaders } from "./http/apiHeaders";
import { getApiUrl } from "./ProxyConfig";
import { conversationsClient } from "./TwilioConversations.service";

import type { RegalAuthProfile, ResetPasswordPayload, User, UserRole } from "Types/Auth";

export const logger = getLogger("Authentication");

type SignInWithSessionOptions = {
    sessionToken: string;
};

function getAuthUrl(path: string): string {
    return `${getApiUrl()}/auth${path}`;
}

export async function signInWithSession({ sessionToken }: SignInWithSessionOptions): Promise<RegalAuthProfile> {
    const response = await fetch(getAuthUrl("/session/create"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ sessionToken }),
    });

    if (!response.ok) {
        throw new Error("Failed to create session cookie");
    }

    const profile: RegalAuthProfile = await response.json();
    return profile;
}

export async function signInWithCredentials(username: string, password: string): Promise<RegalAuthProfile> {
    const response = await fetch(getAuthUrl("/sign-in"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
        throw new Error("Failed to sign in");
    }

    const profile: RegalAuthProfile = await response.json();
    return profile;
}

async function destroyRegalSessionCookie(): Promise<void> {
    await fetch(getAuthUrl("/session/destroy"), {
        method: "POST",
        credentials: "include",
        headers: apiHeaders(),
    });
}

export type SetupProfileOptions = {
    sessionToken: string;
};

export function checkSessionRequest() {
    return fetch(getAuthUrl("/session/check"), {
        credentials: "same-origin",
    });
}

/**
 * Tell Okta the current session is still in use, and refresh regal-sid cookies
 * to the new expiry. Call this after meaningful user activity — not from the
 * session-validity poll, which would defeat idle timeout.
 */
export async function refreshOktaSession(): Promise<void> {
    const response = await fetch(getAuthUrl("/session/refresh"), {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...apiHeaders(),
        },
    });

    if (!response.ok) {
        throw new Error("Failed to refresh session");
    }
}

// eventually these should move to some auth state teardown logic
export function logoutCleanup(): void {
    disconnectSSE();
    reduxStore.dispatch(clearBrand());
    conversationsClient?.shutdown();
    sessionStorage.clear();
    notification.destroy();
}

export async function setAgentOffline(): Promise<void> {
    const state = reduxStore.getState();
    const agentInformation = selectAgentInformation(state);
    if (!agentInformation) {
        logger.error("Could not set agent to be offline because agent information was not found");
        return;
    }

    const availableStatuses = selectBrandAvailableStatuses(state);
    if (!availableStatuses || availableStatuses.length === 0) {
        logger.warn("No available statuses were found");
        return;
    }
    const currentStatusName = agentInformation.status;
    if (currentStatusName) {
        const currentActivity = availableStatuses.find((status) => status.name === currentStatusName);
        if (currentActivity && !currentActivity.available) {
            return;
        }
    }

    let offlineActivity = availableStatuses.find((status) => status.name === "Offline");

    if (!offlineActivity) {
        offlineActivity = availableStatuses.find((status) => !status.available);
    }

    if (!offlineActivity) {
        logger.warn("Could not set agent to be offline because an offline status was not found", {
            availableStatuses,
        });

        return;
    }

    try {
        await apolloMutation({
            mutation: updateAgent,
            variables: {
                updateUserData: {
                    status: offlineActivity.name,
                    twilioSid: agentInformation.workerSid,
                },
            },
        });
        // update type to unknown and use normalizeError
    } catch (error) {
        logger.error("Error attempting to update agent status to offline", {
            error,
            twilioWorkerSid: agentInformation.workerSid,
            status: offlineActivity.name,
        });
    }
}

// make this private so that it's only controlled by this file
let IS_CLIENT_LOGGING_OUT = false;

/**
 * Provides state of the logout process to prevent unnecessary actions
 */
export const isClientLoggingOut = () => IS_CLIENT_LOGGING_OUT;

export async function logout(): Promise<void> {
    IS_CLIENT_LOGGING_OUT = true;
    await setAgentOffline();
    logoutCleanup();
    await destroyRegalSessionCookie();
    // force redirect/code level refresh
    hardRedirect("/login");
}

export async function kickOut() {
    logoutCleanup();
    await destroyRegalSessionCookie();
    hardRedirect("/login?expired=true");
}

export const sendResetEmail = async (email: string): Promise<boolean> => {
    const response = await fetch(getAuthUrl("/send/reset-email"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        throw new Error("Failed to send reset email");
    }

    return true;
};
export const checkPasswordRecoveryToken = async (recoveryToken: string): Promise<{ email: string }> => {
    const response = await fetch(getAuthUrl("/verify/reset-token"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ recoveryToken }),
    });

    if (!response.ok) {
        throw new Error("Failed to validate recovery token");
    }

    return await response.json();
};

export const resetPassword = async ({
    newPassword,
    recoveryToken,
}: ResetPasswordPayload): Promise<{ success: boolean }> => {
    const response = await fetch(getAuthUrl("/reset/password"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ newPassword, recoveryToken }),
    });

    const content = await response.json();

    if (!response.ok) {
        throw new Error("message" in content ? content.message : "Failed to reset password");
    }

    return content;
};

export function useBrandSlug(): string {
    return brandSlugFromAuth();
}

export function userHasRoles(user: NullOr<{ roles: string[] }>, roles: UserRole[]): boolean {
    if (!user) {
        return false;
    }
    return intersection(user.roles || [], roles).length > 0;
}

export function useUserInfo(): NullOr<User> {
    return authState.user || null;
}

export function isRegalVoiceUser(): boolean {
    const userEmail = authState.user?.email || "";
    return userEmail.endsWith("@regalvoice.com") || userEmail.endsWith("@regal.io") || userEmail.endsWith("@regal.ai");
}
